import {useMemo} from 'react'

import {findByFieldName, QuantityRange, useQuantity} from '../../../../fields'
import {OrderStatus} from '../../../types'
import {useChangeableFields} from '../../changeableFields'
import {NOT_EDITABLE_FIELD} from '../../changeableFields/consts'
import {useOrderChangeContext} from '../../context'

import {useOrderConfirmationContext} from './useOrderConfirmationContext'

/**
 * Order confirmation scenario types
 */
type OrderConfirmationScenario =
  | {type: 'default'; range: QuantityRange}
  | {
      type: 'orderConfirmation'
      range: QuantityRange
      allowsPendingDispatchApproval: true
      autoConfirmableRange: QuantityRange
      requireDispatchConfirmation: boolean
    }
  | {
      type: 'orderConfirmation'
      range: QuantityRange
      allowsPendingDispatchApproval: false
    }

const ORDER_CONFIRMATION_STATUSES = [
  OrderStatus.Pending,
  OrderStatus.Reserved,
  OrderStatus.Confirmed
]

/**
 * Hook to determine current order confirmation scenario based on field changeability states and order status.
 *
 * Note: This hook focuses on the quantity field as it's the primary field affected by order confirmation rules and order confirmation feature.
 * Not to confuse with useOrderConfirmationUIState which determines the UI state for the confirmation button/section based on the changeability of the isConfirmedOrder field.
 *
 * Business Rules:
 * - Order Confirmation applies only to orders with status: Pending, Reserved, or Confirmed
 * - For other order statuses, always returns default scenario
 *
 * - Scenario A (default): Out of scope of order confirmation
 *   → Returns when order status not in ORDER_CONFIRMATION_STATUSES
 *   → Returns when quantity is autoConfirmable in base state
 *   → Returns when isConfirmedOrder is not autoConfirmable
 *   → Returns base quantity range
 *
 * - Scenario B (orderConfirmation + dispatch): Quantity requires dispatch confirmation in base, order confirmation auto-confirmable
 *   → Returns base range for input, auto-confirmable range for UI messages, and dynamic dispatch confirmation state
 *   → requireDispatchConfirmation is determined by final field state (after order confirmation rules)
 *
 * - Scenario C (orderConfirmation + restricted): Quantity not changeable in base, order confirmation auto-confirmable
 *   → Returns restricted auto-confirmable range for input (limits user input to allowed range)
 *
 * @returns {OrderConfirmationScenario} Current scenario with relevant configuration
 */
export const useOrderConfirmationScenario = (): OrderConfirmationScenario => {
  const {processData: order} = useOrderChangeContext()
  const {quantityConfiguration: baseRange} = useQuantity()
  const {changeableFields: baseChangeableFields} = useChangeableFields('base')
  const {changeableFields} = useChangeableFields('final')
  const confirmationContext = useOrderConfirmationContext()

  return useMemo(() => {
    if (
      !ORDER_CONFIRMATION_STATUSES.includes(order.orderStatus) ||
      !confirmationContext.isEnabled
    ) {
      return {type: 'default', range: baseRange}
    }

    const baseQuantityField =
      findByFieldName(baseChangeableFields, {field: 'quantity.volume'}) ?? NOT_EDITABLE_FIELD
    const isConfirmedOrderField =
      findByFieldName(baseChangeableFields, {field: 'isConfirmedOrder'}) ?? NOT_EDITABLE_FIELD

    // Scenario A: Default - out of scope of order confirmation
    if (
      baseQuantityField.state === 'autoConfirmable' ||
      isConfirmedOrderField.state !== 'autoConfirmable'
    ) {
      return {type: 'default', range: baseRange}
    }

    // Get final quantity field state (after order confirmation rules)
    const finalQuantityField =
      findByFieldName(changeableFields, {field: 'quantity.volume'}) ?? NOT_EDITABLE_FIELD

    const {quantityRange: autoConfirmableRange} = confirmationContext

    // Scenario B: Dispatch confirmation allowed (quantity requires dispatch confirmation in base state)
    if (baseQuantityField.state === 'requireDispatchConfirmation') {
      return {
        type: 'orderConfirmation',
        range: baseRange, // Use full range for input
        allowsPendingDispatchApproval: true,
        autoConfirmableRange,
        requireDispatchConfirmation: finalQuantityField.state === 'requireDispatchConfirmation'
      }
    }

    // Scenario C: Restricted to auto-confirmable range (quantity not changeable in base state)
    // Only when quantity is explicitly 'notChangeable' (time-based restriction)
    if (baseQuantityField.state === 'notChangeable') {
      return {
        type: 'orderConfirmation',
        range: autoConfirmableRange, // Use restricted range for input
        allowsPendingDispatchApproval: false
      }
    }

    // Fallback to default for any other edge cases
    return {type: 'default', range: baseRange}
  }, [order.orderStatus, baseChangeableFields, changeableFields, baseRange, confirmationContext])
}
